// noinspection SpellCheckingInspection

import ClearIcon from "@mui/icons-material/Clear";
import CloseIcon from "@mui/icons-material/Close";
import FilterListIcon from "@mui/icons-material/FilterList";
import Autocomplete from "@mui/material/Autocomplete";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import MuiDrawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import { styled } from "@mui/material/styles";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { skipToken } from "@reduxjs/toolkit/query";
import { OrderDirection } from "_shared/_api/ordering/order-direction";
import PaginationParameters from "_shared/_api/queries/pagination/pagination-parameters";
import { useAttorneyHubDispatch } from "_shared/_stores/redux-store";
import { addSpacesToTitleCase } from "_shared/_utilities/add-spaces-to-title-case";
import {
  getEntityMembers,
  populateEntityMembersByLegalEntityId,
} from "_shared/entity-members/_stores/entity-members-redux-slice";
import EntityMember from "_shared/entity-members/entity-member";
import DocumentQuery, {
  DocumentFilterParameters,
  DocumentOrderField,
  DocumentOrderParameters,
} from "documents/_api/queries/document-query";
import {
  getDocumentsByQuery,
  getDocumentsQueryByInterface,
  getErrorLoadingDocumentsByQuery,
  getIsLoadingDocumentsByQuery,
  populateDocumentsByQuery,
  updateDocumentsInterfaceQuery,
} from "documents/_stores/documents-redux-slice";
import Document from "documents/document";
import UnavailableDocument from "documents/types/unavailable-documents/unavailable-document";
import {
  getIndividualsByEntity,
} from "marketplace/individuals/_stores/individuals-redux-slice";
import { useGetIndividualsByEntityIdQuery } from "marketplace/individuals/_api/individual-api";
import Individual from "marketplace/individuals/individual";
import { ChildResourceType, ResourceCategory } from "permissions/resources/types";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router";
import { useSession } from "users/__session/session-context";
import { ProposalBoxType, ProposalContext, ProposalOrderField } from "work/proposals/__constants/constants";
import { useGetProposalsQuery } from "work/proposals/_api/proposal-api";
import ProposalQuery, {
  ProposalFilterParameters,
  ProposalOrderParameters,
} from "work/proposals/_api/queries/proposal-query";
import {
  getProposalQueryByInterface,
  isProposalLoadIssue,
  updateProposalInterfaceQuery,
} from "work/proposals/_store/proposals-redux-slice";
import Proposal from "work/proposals/proposal";
import { WorkAgreementOrderField } from "work/work-agreements/_api/queries/_fields/work-agreement-order-field";
import WorkAgreementQuery, {
  WorkAgreementFilterParameters,
  WorkAgreementOrderParameters,
} from "work/work-agreements/_api/queries/work-agreement-query";
import {
  getWorkAgreementQueryByInterface,
  isWorkAgreementLoadIssue,
  updateWorkAgreementInterfaceQuery,
} from "work/work-agreements/_store/work-agreement-redux-slice";
import WorkAgreement from "work/work-agreements/work-agreement";
import { useGetWorkAgreementsQuery } from "work/work-agreements/_api/work-agreement-api";

const Drawer = styled(MuiDrawer)(() => ({
  boxSizing: "border-box",
  flexShrink: 0,
  whiteSpace: "nowrap",
}));
const PermissionsFilterPanel = styled(Drawer, {
  shouldForwardProp: (prop) => prop !== "closeAfterTransition",
})(({ theme }) => ({
  position: "fixed",
  zIndex: theme.zIndex.modal - 1,
}));
const PermissionsFilterPanelContainer = styled("div")(() => ({
  flex: 1,
  height: "100%",
}));
const PermissionsFilterPanelContent = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  height: "100%",
  padding: theme.spacing(2),
  width: "400px",
}));
const TitleBar = styled("div")(({ theme }) => ({
  alignItems: "center",
  display: "flex",
  justifyContent: "space-between",
  flexDirection: "row",
  paddingBottom: theme.spacing(1),
}));
const ActionButtons = styled("div")(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "repeat(2, 1fr)",
  gap: theme.spacing(1),
  marginTop: theme.spacing(2),
  width: "100%",
}));
const AccessTypeContainer = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1),
  marginTop: theme.spacing(2),
}));

type PermissionsFilterDrawerProps = {
  isOpen: boolean;
  onClose: () => void;
  onFilter: () => void;
};

export default function PermissionsFilterDrawer(props: Readonly<PermissionsFilterDrawerProps>) {
  const { isOpen, onClose, onFilter } = props;

  // General State
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [selectedEntityMembers, setSelectedEntityMembers] = useState<EntityMember[]>([]);
  const [selectedIndividuals, setSelectedIndividuals] = useState<Individual[]>([]);
  const [selectedAccessType, setSelectedAccessType] = useState<string | null>(null);

  // Hooks
  const session = useSession();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAttorneyHubDispatch();

  // Entity Members
  const entityMembers = useSelector(getEntityMembers(session.user!.legalEntityId));

  // Entity Member Individuals
  const entityMemberIndividuals = useSelector(getIndividualsByEntity(session.user!.legalEntityId));
  useGetIndividualsByEntityIdQuery({ legalEntityId: session.user!.legalEntityId.value });

  // Documents Query
  const documentsInterfaceName = "contractsIndex";
  const documentQuery = useSelector(getDocumentsQueryByInterface(documentsInterfaceName));
  const documentsResponse = useSelector(getDocumentsByQuery(documentQuery));
  const documentsLoading = useSelector(getIsLoadingDocumentsByQuery(documentQuery));
  const documentsLoadingError = useSelector(getErrorLoadingDocumentsByQuery(documentQuery));

  // Proposals Query
  const proposalsInterfaceName = "proposalsPage";
  const proposalQueryParams = useSelector(getProposalQueryByInterface(proposalsInterfaceName));
  const proposalQuery = useGetProposalsQuery(proposalQueryParams ? {
    query: proposalQueryParams,
    interfaceName: proposalsInterfaceName
  } : skipToken);

  // Work Agreements Query
  const workAgreementsInterfaceName = "workAgreementIndex";
  const workAgreementQueryParams = useSelector(getWorkAgreementQueryByInterface(workAgreementsInterfaceName));
  const workAgreementQuery = useGetWorkAgreementsQuery(workAgreementQueryParams ? {
    query: workAgreementQueryParams,
    interfaceName: workAgreementsInterfaceName
  } : skipToken);

  // Proposals State
  const [selectedProposals, setSelectedProposals] = useState<Proposal[]>([]);

  // Documents State
  const [selectedDocuments, setSelectedDocuments] = useState<(Document | UnavailableDocument)[]>([]);

  // Work Agreements State
  const [selectedWorkAgreements, setSelectedWorkAgreements] = useState<WorkAgreement[]>([]);

  useEffect(() => {
    dispatch(
      populateEntityMembersByLegalEntityId({
        session: session,
        legalEntityId: session.user!.legalEntityId,
      }),
    );

    if (!documentQuery) {
      dispatch(
        updateDocumentsInterfaceQuery({
          interfaceName: documentsInterfaceName,
          query: new DocumentQuery({
            orderParameters: new DocumentOrderParameters({
              orderBy: DocumentOrderField.Name,
              orderDir: OrderDirection.Ascending,
            }),
            filterParameters: new DocumentFilterParameters({}),
            paginationParameters: PaginationParameters.create({
              pageIndex: 0,
              pageSize: 25,
            }),
          }),
        }),
      );
    }

    if (documentQuery && !documentsResponse && !documentsLoading && !documentsLoadingError) {
      dispatch(
        populateDocumentsByQuery({
          session: session,
          query: documentQuery,
        }),
      );
    }

    if (!proposalQueryParams) {
      dispatch(
        updateProposalInterfaceQuery({
          interfaceName: proposalsInterfaceName,
          query: new ProposalQuery({
            filterParameters: new ProposalFilterParameters({
              context: session.context?.viewingAsVendor ? ProposalContext.Vendor : ProposalContext.Client,
              boxType: ProposalBoxType.AllActive,
            }),
            orderParameters: new ProposalOrderParameters({
              orderBy: ProposalOrderField.Created,
              orderDir: OrderDirection.Descending,
            }),
            paginationParameters: PaginationParameters.create({
              pageIndex: 0,
              pageSize: 25,
            }),
          }),
        }),
      );
    }

    if (!workAgreementQueryParams) {
      dispatch(
        updateWorkAgreementInterfaceQuery({
          interfaceName: workAgreementsInterfaceName,
          query: WorkAgreementQuery.create({
            filterParameters: WorkAgreementFilterParameters.create({}),
            orderParameters: WorkAgreementOrderParameters.create({
              orderBy: WorkAgreementOrderField.Start,
              orderDirection: OrderDirection.Descending,
            }),
            paginationParameters: PaginationParameters.create({ pageIndex: 0, pageSize: 25 }),
          }),
        }),
      );
    }
  }, [
    dispatch,
    documentQuery,
    documentsLoading,
    documentsLoadingError,
    documentsResponse,
    entityMemberIndividuals,
    proposalQueryParams,
    session,
    workAgreementQueryParams,
  ]);

  const loadedProposals = useMemo(() => {
    const proposals = proposalQuery.data;
    if (!session.user || !proposals) return [];

    const result: Proposal[] = [];
    for (const proposalJson of proposals?.data ?? []) {
      if (isProposalLoadIssue(proposalJson)) continue;
      const proposal = Proposal.fromJSON({ json: proposalJson, currentUser: session.user });
      if (proposal) result.push(proposal);
    }
    return result;
  }, [proposalQuery.data, session.user]);

  const loadedWorkAgreements = useMemo(() => {
    const workAgreements = workAgreementQuery.data;
    if (!workAgreements) return [];

    const result: WorkAgreement[] = [];
    for (const workAgreementJson of workAgreements?.data ?? []) {
      if (isWorkAgreementLoadIssue(workAgreementJson)) continue;
      const workAgreement = WorkAgreement.fromJSON(workAgreementJson);
      if (workAgreement) result.push(workAgreement);
    }
    return result;
  }, [workAgreementQuery.data]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const categories = searchParams.getAll("categories");
    const granteeUserIds = searchParams.getAll("granteeUserIds");
    const accessType = searchParams.get("accessType");
    const documentIds = searchParams.getAll("documentIds");
    const workAgreementIds = searchParams.getAll("workAgreementIds");

    // eslint-disable-next-line react-hooks/set-state-in-effect
    setSelectedCategories(categories);

    const selectedEntityMemberIds = granteeUserIds
      .map((userId) => entityMembers?.find((member) => member.userId.value === userId))
      .filter((member): member is EntityMember => member !== undefined);
    setSelectedEntityMembers(selectedEntityMemberIds);

    const selectedIndividuals =
      entityMemberIndividuals?.filter((individual) =>
        selectedEntityMemberIds.some((member) => member.userId.isEqualTo(individual.userId)),
      ) ?? [];
    setSelectedIndividuals(selectedIndividuals);

    setSelectedAccessType(accessType);
    setSelectedDocuments(
      documentIds
        .map((docId) => documentsResponse?.data.find((doc) => doc.id.value === docId))
        .filter((doc): doc is Document => doc !== undefined),
    );

    setSelectedWorkAgreements(
      workAgreementIds
        .map((waId) => loadedWorkAgreements.find((wa) => wa.id?.value === waId))
        .filter((wa): wa is WorkAgreement => wa !== undefined),
    );
  }, [location.search, entityMembers, entityMemberIndividuals, documentsResponse?.data, loadedWorkAgreements]);

  const filtersChanged = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    const hasParentParam = searchParams.get("hasParent") === "true";
    const categories = searchParams.getAll("categories");
    const granteeUserIds = searchParams.getAll("granteeUserIds");
    const accessType = searchParams.get("accessType");
    const documentIds = searchParams.getAll("documentIds");
    const proposalIds = searchParams.getAll("proposalIds");
    const workAgreementIds = searchParams.getAll("workAgreementIds");

    const selectedEntityMemberIds = selectedEntityMembers.map((u) => u.userId.value);
    const selectedDocumentIds = selectedDocuments.map((d) => d.id.value);
    const selectedProposalsIds = selectedProposals.map((p) => p.id?.value);
    const selectedWorkAgreementIds = selectedWorkAgreements.map((wa) => wa.id?.value);

    const currentFilters = {
      categories: selectedCategories,
      granteeUserIds: selectedEntityMemberIds,
      accessType: selectedAccessType,
      documentIds: selectedDocumentIds,
      proposalIds: selectedProposalsIds,
      workAgreementIds: selectedWorkAgreementIds,
      hasParent:
        hasParentParam ||
        selectedCategories.length > 0 ||
        selectedEntityMemberIds.length > 0 ||
        selectedAccessType !== null ||
        selectedDocumentIds.length > 0 ||
        selectedProposals.length > 0 ||
        selectedWorkAgreements.length > 0,
    };

    const urlFilters = {
      hasParent: hasParentParam,
      categories,
      granteeUserIds,
      accessType,
      documentIds,
      proposalIds,
      workAgreementIds,
    };

    return JSON.stringify(currentFilters) !== JSON.stringify(urlFilters);
  }, [
    location.search,
    selectedCategories,
    selectedEntityMembers,
    selectedAccessType,
    selectedDocuments,
    selectedProposals,
    selectedWorkAgreements,
  ]);

  function handleFilterClicked() {
    const selectedEntityMemberIds = selectedEntityMembers.map((u) => u.userId.value);
    const selectedDocumentIds = selectedDocuments.map((d) => d.id.value);
    const selectedProposalsIds = selectedProposals.map((p) => p.id?.value);
    const selectedWorkAgreementIds = selectedWorkAgreements.map((wa) => wa.id?.value);

    const filters = {
      categories: selectedCategories,
      granteeUserIds: selectedEntityMemberIds,
      accessType: selectedAccessType,
      documentIds: selectedDocumentIds,
      proposalIds: selectedProposalsIds,
      workAgreementIds: selectedWorkAgreementIds,
      hasParent:
        selectedCategories.length > 0 ||
        selectedEntityMemberIds.length > 0 ||
        selectedAccessType !== null ||
        selectedDocumentIds.length > 0 ||
        selectedProposalsIds.length > 0 ||
        selectedWorkAgreementIds.length > 0,
    };

    // Update URL search params with new filters
    const searchParams = new URLSearchParams();

    if (filters.hasParent) {
      searchParams.append("hasParent", String(filters.hasParent));
    }

    for (const c of filters.categories) {
      searchParams.append("categories", c);
    }

    for (const u of filters.granteeUserIds) {
      searchParams.append("granteeUserIds", u);
    }

    if (filters.accessType) {
      searchParams.append("accessType", filters.accessType);
    }

    for (const d of filters.documentIds) {
      searchParams.append("documentIds", d);
    }

    for (const p of filters.proposalIds) {
      searchParams.append("proposalIds", p ?? "");
    }

    for (const wa of filters.workAgreementIds) {
      searchParams.append("workAgreementIds", wa ?? "");
    }

    navigate({ search: searchParams.toString() });

    onFilter();
  }

  function handleClearFilters() {
    const searchParams = new URLSearchParams();
    navigate({ search: searchParams.toString() });

    setSelectedCategories([]);
    setSelectedEntityMembers([]);
    setSelectedAccessType(null);
    setSelectedDocuments([]);
    setSelectedProposals([]);
    setSelectedWorkAgreements([]);

    onFilter();
  }

  return (
    <PermissionsFilterPanel
      open={isOpen}
      anchor="right"
      variant="temporary"
      onClose={onClose}
      ModalProps={{ keepMounted: true }}
    >
      <PermissionsFilterPanelContainer>
        <PermissionsFilterPanelContent>
          <TitleBar>
            <Typography variant="h5">Filter Additional Permissions</Typography>
            <Tooltip title="Close panel">
              <IconButton size="medium" onClick={onClose}>
                <CloseIcon fontSize="large" />
              </IconButton>
            </Tooltip>
          </TitleBar>
          <Stack gap={3} marginTop={2}>
            <Autocomplete
              multiple
              options={Object.keys(ResourceCategory).filter((key) => Number.isNaN(Number(key)))}
              renderInput={(params) => <TextField {...params} label="Categories" placeholder="Search For Category" />}
              value={selectedCategories}
              onChange={(_event, newValue) => {
                setSelectedCategories(newValue);
              }}
            />
            <Autocomplete
              multiple
              options={entityMemberIndividuals ?? []}
              getOptionLabel={(option) => option.fullName.toString()}
              renderInput={(params) => <TextField {...params} label="Users" placeholder="Search For User" />}
              value={selectedIndividuals}
              onChange={(_event, newValue) => {
                const selectedMembers =
                  entityMembers?.filter((member) =>
                    newValue.some((individual) => individual.userId.isEqualTo(member.userId)),
                  ) ?? [];
                setSelectedEntityMembers(selectedMembers);
                setSelectedIndividuals(newValue);
              }}
            />
            <Card variant="outlined" sx={{ borderRadius: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Access Type
                </Typography>
                <AccessTypeContainer>
                  <Autocomplete
                    options={Object.keys(ChildResourceType)
                      .filter((key) => Number.isNaN(Number(key)))
                      .map((key) => addSpacesToTitleCase(key.replace("Access", "")))}
                    renderInput={(params) => (
                      <TextField {...params} label="Filter By Access Type" placeholder="Search For Access Type" />
                    )}
                    value={selectedAccessType}
                    onChange={(_event, newValue) => {
                      setSelectedAccessType(newValue);
                    }}
                  />
                  {selectedAccessType === "Document" && (
                    <Autocomplete
                      multiple
                      options={documentsResponse?.data ?? []}
                      getOptionLabel={(option) => {
                        if ("issue" in option) return "Unavailable Document";
                        return option.name?.toString() ?? "Unknown Document";
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={selectedAccessType ? `Filter By ${selectedAccessType}` : ""}
                          placeholder={selectedAccessType ? "Search For Document" : "Select an access type above"}
                        />
                      )}
                      value={selectedDocuments}
                      onChange={(_event, newValue) => {
                        setSelectedDocuments(newValue);
                      }}
                    />
                  )}
                  {selectedAccessType === "Proposal" && (
                    <Autocomplete
                      multiple
                      options={loadedProposals}
                      getOptionLabel={(option) => option.name?.toString() ?? "Unknown Proposal"}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={selectedAccessType ? `Filter By ${selectedAccessType}` : ""}
                          placeholder={selectedAccessType ? "Search For Proposal" : "Select an access type above"}
                        />
                      )}
                      value={selectedProposals}
                      onChange={(_event, newValue) => {
                        setSelectedProposals(newValue);
                      }}
                    />
                  )}
                  {selectedAccessType === "Work Agreement" && (
                    <Autocomplete
                      multiple
                      options={loadedWorkAgreements}
                      getOptionLabel={(option) => option.name?.toString() ?? "Unknown Work Agreement"}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={selectedAccessType ? `Filter By ${selectedAccessType}` : ""}
                          placeholder={selectedAccessType ? "Search For Work Agreement" : "Select an access type above"}
                        />
                      )}
                      value={selectedWorkAgreements}
                      onChange={(_event, newValue) => {
                        setSelectedWorkAgreements(newValue);
                      }}
                    />
                  )}
                </AccessTypeContainer>
              </CardContent>
            </Card>
            <ActionButtons>
              <Button
                variant="contained"
                color="primary"
                disabled={!filtersChanged}
                startIcon={<FilterListIcon />}
                onClick={handleFilterClicked}
              >
                Apply
              </Button>
              <Button
                variant="outlined"
                color="primary"
                startIcon={<ClearIcon />}
                disabled={
                  !selectedCategories.length &&
                  !selectedEntityMembers.length &&
                  !selectedAccessType &&
                  !selectedDocuments.length &&
                  !selectedProposals.length &&
                  !selectedWorkAgreements.length
                }
                onClick={handleClearFilters}
              >
                Clear
              </Button>
            </ActionButtons>
          </Stack>
        </PermissionsFilterPanelContent>
      </PermissionsFilterPanelContainer>
    </PermissionsFilterPanel>
  );
}
